import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';
import { getConfig, get as coreGet, getMany as coreGetMany, set as coreSet, setMany as coreSetMany, setFile as coreSetFile, getFiles as coreGetFiles, search as coreSearch, runQuery as coreRunQuery, runQueryMany as coreRunQueryMany, runExpression as coreRunExpression, runExpressionMany as coreRunExpressionMany, count as coreCount, aggregate as coreAggregate, queryAggregate as coreQueryAggregate, subscribe as coreSubscribe, functions as coreFunctions, live as coreLive, reconcileSetResult as coreReconcileSetResult, waitForSetResult as coreWaitForSetResult, ServerSessionManager, } from '@bounded-sh/core';
import { SolanaKeypairProvider } from './auth/providers/solana-keypair-provider';
import { OffchainAuthProvider } from './auth/providers/offchain-auth-provider';
import { stripCallerAuthHeaders } from './sanitize-headers';
/* ------------------------------------------------------------------ */
/*  Keypair parsing                                                    */
/* ------------------------------------------------------------------ */
function parseKeypair(secret) {
    const trimmed = secret.trim();
    try {
        const secretKey = trimmed.startsWith('[')
            ? Uint8Array.from(JSON.parse(trimmed))
            : bs58.decode(trimmed);
        return Keypair.fromSecretKey(secretKey);
    }
    catch (_a) {
        throw new Error('Invalid keypair: must be base58 or JSON array.');
    }
}
/**
 * Whether a JWT is expired or within 60s of expiry (or unparseable). Used to
 * decide if the wallet should re-sign for a fresh token. Server-only (Node
 * Buffer); a keypair-backed session can always re-sign, so this is safe to be
 * conservative about.
 */
function jwtExpiringSoon(token) {
    if (!token)
        return true;
    try {
        const part = token.split('.')[1];
        const json = Buffer.from(part.replace(/-/g, '+').replace(/_/g, '/'), 'base64').toString('utf8');
        const exp = JSON.parse(json).exp;
        if (!exp)
            return false;
        return Date.now() > exp * 1000 - 60000;
    }
    catch (_a) {
        return true;
    }
}
/* ------------------------------------------------------------------ */
/*  WalletClient                                                       */
/* ------------------------------------------------------------------ */
export class WalletClient {
    constructor(provider, sessionManager, address, config) {
        this.provider = provider;
        this.sessionManager = sessionManager;
        this.address = address;
        this.config = config;
        this.live = {
            intent: (roomPath, intent, opts = {}) => coreLive.intent(roomPath, intent, this.mergeLiveIntentOptions(opts)),
            // Route status through the SAME auth merge intent uses so a server-keypair
            // agent's session token rides the request. Without it the agent sends the
            // public app id only and the server returns the slim anonymous shape rather
            // than the full diagnostics the authenticated agent is entitled to (#102).
            status: (roomPath, opts = {}) => coreLive.status(roomPath, this.mergeLiveStatusOptions(opts)),
            // Agent-as-player view stream: subscribe to THIS wallet's per-player view
            // under the wallet's own session (per-subscription overrides — no global
            // auth needed). The view id defaults to this wallet's @user.id (== its
            // address for keypair identities). This is the missing half of the
            // documented "external agent joins AS a player" pattern: intents could be
            // sent, but the agent could never SEE its view from Node.
            subscribeView: (roomPath, opts) => {
                var _a;
                const ovr = this.buildOverrides();
                return coreLive.subscribeView(roomPath, {
                    userId: (_a = opts.userId) !== null && _a !== void 0 ? _a : this.address,
                    onData: opts.onData,
                    onError: opts.onError,
                    _overrides: {
                        _getAuthHeaders: ovr._getAuthHeaders,
                        _clearAuth: ovr._clearAuth,
                        _walletAddress: ovr._walletAddress,
                        _config: ovr._config,
                    },
                });
            },
        };
    }
    /* ---- Auth override helpers ---- */
    /**
     * Return this wallet client's current bearer token, refreshing its isolated
     * keypair session first when the cached token is expired or near expiry.
     */
    async getIdToken() {
        let session = await this.sessionManager.getSession();
        if (jwtExpiringSoon(session.idToken)) {
            this.sessionManager.clearSession();
            session = await this.sessionManager.getSession();
        }
        return session.idToken;
    }
    buildOverrides() {
        return {
            authProvider: this.provider,
            // Pin every request to this wallet client's snapshotted app + endpoints so
            // a later global init() can never retarget its reads/writes (CTA-05).
            _config: this.config,
            _getAuthHeaders: async () => {
                return { Authorization: `Bearer ${await this.getIdToken()}` };
            },
            _clearAuth: async () => {
                this.sessionManager.clearSession();
            },
            _walletAddress: this.address,
        };
    }
    /** #023: strip caller Authorization (any casing) so operations run as the isolated wallet. */
    sanitizeHeaders(headers) {
        return stripCallerAuthHeaders(headers);
    }
    mergeSetOverrides(opts) {
        var _a;
        const ovr = this.buildOverrides();
        return Object.assign(Object.assign({}, opts), { _overrides: Object.assign(Object.assign({}, opts === null || opts === void 0 ? void 0 : opts._overrides), { headers: this.sanitizeHeaders((_a = opts === null || opts === void 0 ? void 0 : opts._overrides) === null || _a === void 0 ? void 0 : _a.headers), authProvider: ovr.authProvider, _getAuthHeaders: ovr._getAuthHeaders, _clearAuth: ovr._clearAuth, _config: ovr._config }) });
    }
    mergeReadOverrides(existing) {
        const ovr = this.buildOverrides();
        return Object.assign(Object.assign({}, existing), { headers: this.sanitizeHeaders(existing === null || existing === void 0 ? void 0 : existing.headers), _getAuthHeaders: ovr._getAuthHeaders, _clearAuth: ovr._clearAuth, _walletAddress: ovr._walletAddress, _config: ovr._config });
    }
    mergeLiveIntentOptions(opts) {
        var _a;
        const ovr = this.buildOverrides();
        const merged = Object.assign(Object.assign({}, opts), { headers: this.sanitizeHeaders(opts === null || opts === void 0 ? void 0 : opts.headers), _overrides: Object.assign(Object.assign({}, opts === null || opts === void 0 ? void 0 : opts._overrides), { headers: this.sanitizeHeaders((_a = opts === null || opts === void 0 ? void 0 : opts._overrides) === null || _a === void 0 ? void 0 : _a.headers), _getAuthHeaders: ovr._getAuthHeaders, _clearAuth: ovr._clearAuth, _walletAddress: ovr._walletAddress, _config: ovr._config }) });
        return merged;
    }
    mergeLiveStatusOptions(opts) {
        var _a;
        const ovr = this.buildOverrides();
        const merged = Object.assign(Object.assign({}, opts), { headers: this.sanitizeHeaders(opts === null || opts === void 0 ? void 0 : opts.headers), _overrides: Object.assign(Object.assign({}, opts === null || opts === void 0 ? void 0 : opts._overrides), { headers: this.sanitizeHeaders((_a = opts === null || opts === void 0 ? void 0 : opts._overrides) === null || _a === void 0 ? void 0 : _a.headers), _getAuthHeaders: ovr._getAuthHeaders, _clearAuth: ovr._clearAuth, _walletAddress: ovr._walletAddress, _config: ovr._config }) });
        return merged;
    }
    /* ---- Data operations ---- */
    async set(path, document, options) {
        return coreSet(path, document, this.mergeSetOverrides(options));
    }
    async setMany(many, options) {
        return coreSetMany(many, this.mergeSetOverrides(options));
    }
    async setFile(path, file, options) {
        return coreSetFile(path, file, { _overrides: this.mergeReadOverrides(options === null || options === void 0 ? void 0 : options._overrides) });
    }
    /**
     * Reconcile a `submitted` write receipt through THIS client's own provider
     * and app binding. A bare top-level `reconcileSetResult` cannot serve a wallet
     * client: the provider and config are private to the instance, and a later
     * global `init()` would otherwise reconcile against the wrong app.
     */
    async reconcileSetResult(receipt, options) {
        return coreReconcileSetResult(receipt, this.mergeReconcileOptions(options));
    }
    /** Bounded repeat of {@link reconcileSetResult}; see `waitForSetResult`. */
    async waitForSetResult(receipt, options) {
        return coreWaitForSetResult(receipt, this.mergeReconcileOptions(options));
    }
    mergeReconcileOptions(opts) {
        var _a;
        const ovr = this.buildOverrides();
        return Object.assign(Object.assign({}, (opts !== null && opts !== void 0 ? opts : {})), { _overrides: Object.assign(Object.assign({}, opts === null || opts === void 0 ? void 0 : opts._overrides), { headers: this.sanitizeHeaders((_a = opts === null || opts === void 0 ? void 0 : opts._overrides) === null || _a === void 0 ? void 0 : _a.headers), authProvider: ovr.authProvider, _getAuthHeaders: ovr._getAuthHeaders, _clearAuth: ovr._clearAuth, _walletAddress: ovr._walletAddress, _config: ovr._config }) });
    }
    async get(path, opts) {
        return coreGet(path, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    async getMany(paths, opts) {
        return coreGetMany(paths, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides() }));
    }
    async getFiles(path, options) {
        return coreGetFiles(path, { _overrides: this.mergeReadOverrides(options === null || options === void 0 ? void 0 : options._overrides) });
    }
    async search(path, query, opts = {}) {
        return coreSearch(path, query, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    async runQuery(absolutePath, queryName, queryArgs, opts) {
        return coreRunQuery(absolutePath, queryName, queryArgs, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    async runQueryMany(many, opts) {
        return coreRunQueryMany(many, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    async runExpression(expression, queryArgs, options) {
        return coreRunExpression(expression, queryArgs, Object.assign(Object.assign({}, options), { _overrides: this.mergeReadOverrides(options === null || options === void 0 ? void 0 : options._overrides) }));
    }
    async runExpressionMany(many) {
        const ovr = this.mergeReadOverrides();
        return coreRunExpressionMany(many.map(m => (Object.assign(Object.assign({}, m), { _overrides: ovr }))));
    }
    async count(path, opts = {}) {
        return coreCount(path, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    async aggregate(path, operation, opts = {}) {
        return coreAggregate(path, operation, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    async queryAggregate(path, spec, opts = {}) {
        return coreQueryAggregate(path, spec, Object.assign(Object.assign({}, opts), { _overrides: this.mergeReadOverrides(opts === null || opts === void 0 ? void 0 : opts._overrides) }));
    }
    /**
     * Subscribe to real-time updates as THIS wallet's identity. The WS connection
     * authenticates with the wallet's own session (so read rules see the right
     * principal), and is scoped to its own connection so it never crosses another
     * identity. Accepts a bare callback or `{ onData, onError, filter, ... }`.
     * Returns an async unsubscribe.
     */
    async subscribe(path, options) {
        const opts = typeof options === 'function' ? { onData: options } : Object.assign({}, options);
        const ovr = this.buildOverrides();
        return coreSubscribe(path, Object.assign(Object.assign({}, opts), { _overrides: {
                _getAuthHeaders: ovr._getAuthHeaders,
                _clearAuth: ovr._clearAuth,
                _walletAddress: ovr._walletAddress,
                _config: ovr._config,
            } }));
    }
    /**
     * Invoke a deployed Bounded Function AS this wallet's identity. The dispatcher
     * sees the wallet's verified session, so the function's `auth` rule +
     * `ctx.user` reflect this client. Returns the function's JSON; throws
     * `FunctionInvokeError` on 401/403/404/503.
     */
    async invoke(name, args = {}, opts = {}) {
        const ovr = this.buildOverrides();
        return coreFunctions.invoke(name, args, Object.assign(Object.assign({}, opts), { _overrides: { _getAuthHeaders: ovr._getAuthHeaders, _config: ovr._config } }));
    }
    /* ---- Signing operations (use provider directly) ---- */
    async signMessage(message) {
        return this.provider.signMessage(message);
    }
    async signTransaction(transaction) {
        return this.provider.signTransaction(transaction);
    }
    async signAndSubmitTransaction(transaction, feePayer) {
        return this.provider.signAndSubmitTransaction(transaction, feePayer);
    }
}
/**
 * Create a self-contained wallet client bound to one Solana keypair.
 * Each client has its own auth session — no global state is mutated.
 *
 * @example
 * ```ts
 * const vault = await createWalletClient({ keypair: process.env.VAULT_KEY });
 * await vault.set('markets/123', data);
 * ```
 */
export async function createWalletClient(opts) {
    var _a;
    // Snapshot an IMMUTABLE copy of the resolved config now. Every request this
    // client makes is threaded with this snapshot (as `_config`) and its session
    // manager mints against this app+issuer, so a later global `init(...)` for a
    // different app/environment can never retarget this client's reads, writes,
    // function calls, live intents, or session mint (CTA-05).
    const config = Object.freeze(Object.assign({}, (await getConfig())));
    const sessionConfig = {
        appId: config.appId,
        authApiUrl: config.authApiUrl,
        authMethod: config.authMethod,
    };
    if (!('keypair' in opts) || typeof opts.keypair !== 'string') {
        throw new Error('createWalletClient requires a Solana keypair');
    }
    const kp = parseKeypair(opts.keypair);
    let provider = new SolanaKeypairProvider((_a = config.rpcUrl) !== null && _a !== void 0 ? _a : null, kp);
    if (config.chain === 'offchain') {
        provider = new OffchainAuthProvider(provider);
    }
    const sessionManager = ServerSessionManager.forKeypair(kp, sessionConfig);
    // Eagerly authenticate so failures surface immediately
    await sessionManager.getSession();
    return new WalletClient(provider, sessionManager, kp.publicKey.toBase58(), config);
}
//# sourceMappingURL=wallet-client.js.map